import React from 'react';
import { Switch } from '@rebass/forms';
import classNames from 'classnames';

import { useAuth } from 'context/AuthContext';
import { useToastHelper } from 'hooks/useToastHelper';
import { useUpdateAccountSettingsMutation } from 'api/useUpdateAccountSettingsMutation';
import services from 'services/services';
import auth from 'services/auth';

/**
 * @typedef {Object} PanelistViewStudiesSwitchProps
 * @property {string} [className]
 * @property {string} [id]
 */

/**
 * Toggle for whether panelists can see the "My Studies" section
 * (study list + scheduled sessions + screener links) on their
 * logged-in profile.
 *
 * @param {PanelistViewStudiesSwitchProps & React.InputHTMLAttributes<HTMLInputElement>} props
 */
export function PanelistViewStudiesSwitch({ className, ...rest }) {
    const authContext = useAuth();
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const isEnabled = !!authContext?.account?.settings?.panelist_view_studies_allow;

    const { mutate, isLoading } = useUpdateAccountSettingsMutation({
        onSuccess: () => {
            const newValue = !isEnabled;
            authContext.account.settings.panelist_view_studies_allow = newValue;
            auth.checkAuth();
            addSuccessToast(newValue ? 'Panelists can now view My Studies' : 'Panelists can no longer view My Studies');
        },
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error) || 'Something went wrong. Please try again later.';
            addErrorToast(errorText);
        }
    });

    return (
        <Switch
            role="switch"
            aria-checked={isEnabled}
            aria-label="Allow panelists to view My Studies"
            className={classNames('switch-small', className)}
            checked={isEnabled}
            disabled={isLoading}
            onClick={() => mutate({ panelist_view_studies_allow: !isEnabled })}
            {...rest}
        />
    );
}
